import React, { useState, useEffect } from "react";
import { makeRequestAuth } from "../../../_config/api";
import { ToastContainer } from "react-toastify";
import { showSuccessToast, showErrorToast } from "../../../utils/toastUtils";
import { Calendar, Heart, User, Users, AlertCircle, CheckCircle, Clock, Edit2 } from "lucide-react";

const ArrivalCanadaForm = () => {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState([]);
  const [maritalStatus, setMaritalStatus] = useState("");

  // --- ADDED THIS STATE ---
  const [formData, setFormData] = useState({
    dateOfMarriage: "",
    separationOrDeathDate: ""
  });

  useEffect(() => {
    fetchAPi();
  }, []);

  const fetchAPi = async () => {
    setLoading(true);
    try {
      const response = await makeRequestAuth("get", "/user/getMarital");
      if (response?.data) {
        setPreview(response.data);
        setMaritalStatus(response.data.maritalStatus || "");
        // Update formData when data is fetched
        setFormData({
          dateOfMarriage: response.data.dateOfMarriage || "",
          separationOrDeathDate: response.data.separationOrDeathDate || ""
        });
      }
    } catch (err) {
      if (err?.message === "Basic profile not found") setPreview(null);
    } finally {
      setLoading(false);
    }
  };

  // --- ADDED THIS HANDLER ---
  const handleChange = (name, value) => {
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const validateForm = (form) => {
    let errorList = [];
    const status = form.status.value;
    // Changed to check formData instead of DOM directly for reliability
    const marriageDate = formData.dateOfMarriage;
    const sepDate = formData.separationOrDeathDate;

    if (!status) errorList.push("Marital status is required");
    if (status === "married" && !marriageDate) errorList.push("Date of Marriage is required");
    if (["divorced", "separated", "widow", "widower"].includes(status) && !sepDate)
      errorList.push("Separation / Death date is required");

    return errorList;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const form = e.target;
    const validationErrors = validateForm(form);

    if (validationErrors.length > 0) {
      setErrors(validationErrors);
      return;
    }

    setErrors([]);
    setLoading(true);

    const payload = {
      maritalStatus: maritalStatus, // uses the state
      dateOfMarriage: ['married'].includes(maritalStatus) ? formData.dateOfMarriage : null,
      separationOrDeathDate: !['single', 'married', 'common_law'].includes(maritalStatus) ? formData.separationOrDeathDate : null,
    };

    try {
      const res = await makeRequestAuth("post", "/user/createOrUpdateMarital", payload);
      setPreview(res?.data || payload);
      showSuccessToast("Details saved successfully ✅");
    } catch (err) {
      showErrorToast(err.response?.data?.message || err.message);
    } finally {
      setLoading(false);
    }
  };

  const STATUS_CONFIG = {
    pending: { color: "bg-amber-100 text-amber-700 border-amber-200", icon: <Clock size={14} /> },
    approve: { color: "bg-emerald-100 text-emerald-700 border-emerald-200", icon: <CheckCircle size={14} /> },
    reject: { color: "bg-rose-100 text-rose-700 border-rose-200", icon: <AlertCircle size={14} /> },
  };

  const formatDate = (date) => {
    if (!date) return "N/A";
    return new Date(date).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  return (
    <div className="max-w-2xl mx-auto py-8 px-4">
      <ToastContainer />

      {loading && (
        <div className="fixed inset-0 bg-white/70 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="flex flex-col items-center">
            <div className="w-10 h-10 border-4 border-gray-200 border-t-[#132b45] rounded-full animate-spin"></div>
            <p className="mt-3 text-[#132b45] font-semibold tracking-wide">Processing...</p>
          </div>
        </div>
      )}

      <div className="bg-white rounded-3xl shadow-xl border border-gray-100 overflow-hidden">
        <div className="bg-[#132b45] px-8 py-6 text-white">
          <h2 className="text-xl font-bold flex items-center gap-2">
            <Users size={24} className="text-blue-400" /> Marital Status Details
          </h2>
          <p className="text-blue-100/70 text-sm">Keep your personal status updated for visa processing</p>
        </div>

        <div className="p-8">
          {errors.length > 0 && (
            <div className="mb-6 flex items-start gap-3 bg-rose-50 border border-rose-100 text-rose-700 p-4 rounded-2xl animate-in fade-in slide-in-from-top-2">
              <AlertCircle className="shrink-0 mt-0.5" size={18} />
              <div className="text-sm">
                <p className="font-bold mb-1">Action Required:</p>
                <ul className="list-disc ml-4 space-y-0.5">
                  {errors.map((err, i) => <li key={i}>{err}</li>)}
                </ul>
              </div>
            </div>
          )}

          {preview ? (
            <div className="space-y-6">
              <div className="grid grid-cols-1 gap-4">
                <PreviewCard
                  label="Current Status"
                  value={preview.maritalStatus}
                  icon={<User className="text-[#132b45]" />}
                  capitalize
                />
                {preview.dateOfMarriage && (
                  <PreviewCard
                    label="Date of Marriage"
                    value={formatDate(preview.dateOfMarriage)}
                    icon={<Heart className="text-rose-500" />}
                  />
                )}
                {preview.separationOrDeathDate && (
                  <PreviewCard
                    label="Separation / Event Date"
                    value={formatDate(preview.separationOrDeathDate)}
                    icon={<Calendar className="text-blue-500" />}
                  />
                )}
              </div>

              <div className="pt-6 border-t border-gray-100 flex flex-col sm:flex-row justify-between items-center gap-4">
                <div className={`flex items-center gap-2 px-4 py-1.5 rounded-full border text-xs font-bold uppercase tracking-wider ${STATUS_CONFIG[preview.status?.toLowerCase()]?.color || "bg-gray-100 text-gray-600"}`}>
                  {STATUS_CONFIG[preview.status?.toLowerCase()]?.icon || <Clock size={14} />}
                  {preview.status || "Pending Approval"}
                </div>
                {(preview.status === "reject" || !preview.status) && (
                  <button
                    onClick={() => setPreview(null)}
                    className="flex items-center gap-2 text-sm font-bold text-[#132b45] hover:text-black transition-colors px-4 py-2 rounded-lg hover:bg-gray-50"
                  >
                    <Edit2 size={16} /> Edit My Status
                  </button>
                )}
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6 animate-in fade-in duration-500">
              <div className="space-y-2">
                <label className="text-sm font-bold text-gray-700 ml-1">Marital Status</label>
                <div className="relative">
                  <select
                    name="status"
                    value={maritalStatus}
                    onChange={(e) => setMaritalStatus(e.target.value)}
                    className="w-full appearance-none bg-gray-50 border-2 border-gray-100 rounded-2xl px-5 py-3.5 focus:border-[#132b45] focus:bg-white outline-none transition-all cursor-pointer text-gray-800"
                  >
                    <option value="">Choose your status</option>
                    <option value="single">Single</option>
                    <option value="married">Married</option>
                    <option value="divorced">Divorced</option>
                    <option value="separated">Separated</option>
                    <option value="common_law">Common Law</option>
                    <option value="widow">Widow / Widower</option>
                  </select>
                  <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400">
                    ▼
                  </div>
                </div>
              </div>

              {maritalStatus === "married" && (
                <div className="space-y-2 animate-in zoom-in-95 duration-200">
                  <label className="text-sm font-bold text-gray-700 ml-1">Date of Marriage</label>
                  <input
                    type="date"
                    name="dateOfMarriage"
                    className="w-full bg-gray-50 border-2 border-gray-100 rounded-2xl px-5 py-3.5 focus:border-[#132b45] outline-none"
                    value={formData.dateOfMarriage}
                    onChange={(e) => handleChange("dateOfMarriage", e.target.value)}
                    max={new Date().toISOString().split("T")[0]}
                  />
                </div>
              )}

              {["divorced", "separated", "widow", "widower"].includes(maritalStatus) && (
                <div className="space-y-2 animate-in zoom-in-95 duration-200">
                  <label className="text-sm font-bold text-gray-700 ml-1">Separation / Death Date</label>
                  <input
                    type="date"
                    name="separationOrDeathDate"
                    className="w-full bg-gray-50 border-2 border-gray-100 rounded-2xl px-5 py-3.5 focus:border-[#132b45] outline-none"
                    value={formData.separationOrDeathDate}
                    onChange={(e) => handleChange("separationOrDeathDate", e.target.value)}
                    max={new Date().toISOString().split("T")[0]}
                  />
                </div>
              )}

              <button
                type="submit"
                className="w-full bg-[#132b45] hover:bg-black text-white font-bold py-4 rounded-2xl shadow-lg shadow-blue-900/10 transition-all hover:-translate-y-0.5 active:scale-95"
              >
                Save Details
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

const PreviewCard = ({ label, value, icon, capitalize }) => (
  <div className="flex items-center gap-4 p-4 rounded-2xl bg-gray-50 border border-gray-100 transition-hover hover:border-blue-100 hover:bg-white">
    <div className="p-3 bg-white rounded-xl shadow-sm border border-gray-50">
      {icon}
    </div>
    <div>
      <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest leading-none mb-1">{label}</p>
      <p className={`text-gray-800 font-bold ${capitalize ? 'capitalize' : ''}`}>
        {value || "Not Specified"}
      </p>
    </div>
  </div>
);

export default ArrivalCanadaForm;